import {getLanguageCode} from '../i18n.ts';
import type Map from 'ol/Map';
import type RenderEvent from 'ol/render/Event';
import type {Extent} from 'ol/extent';
import type {Coordinate} from 'ol/coordinate';
import type VectorSource from '../../node_modules/ol/source/Vector.js';

/**
 * This constant establishes the correspondence between WMTS zoom level and printed map scale.
 * E.g. WMTS zoom level 21 with resolution 5 m / px will be used to print at 1 : 50000 scale.
 * The content of zoom level 21 tiles corresponds to the map style and level of generalization
 * of 1 : 50000 paper maps.
 * Another way of stating it is that Swisstopo WMTS maps are printed at 254 dpi.
 * By using the same correspondence for vector features, we make sure that their size relative
 * to the base map is the same in print and on screen.
 */
export const SWISSTOPO_PIXELS_PER_METER = 10000;

export const POINTS_PER_INCH = 72;

// According to the "international yard" definition 1 inch is defined as exactly 2.54 cm.
export const METERS_PER_INCH = 0.0254;

export const POINTS_PER_METER = POINTS_PER_INCH / METERS_PER_INCH;

export const PIXELS_PER_POINT =
  (SWISSTOPO_PIXELS_PER_METER * METERS_PER_INCH) / POINTS_PER_INCH;

export const PRINT_DPI = SWISSTOPO_PIXELS_PER_METER * METERS_PER_INCH;

export const SCALES = [
  10000, 20000, 25000, 50000, 100000, 200000, 500000, 1000000, 1400000,
];

export const ORIENTATIONS = ['landscape', 'portrait'] as const;

export const PORTRAIT_PAGE_SIZES = [510, 710];
export const LANDSCAPE_PAGE_SIZES = [762, 479];

export type Orientations = (typeof ORIENTATIONS)[number];

function getPaperSize(layout: string) {
  if (layout.includes('portrait')) {
    return [510, 710];
  } else {
    return [762, 479];
  }
}

function computeScale(
  layout: string,
  width: number,
  height: number,
  contain = false,
) {
  const paperSize = getPaperSize(layout);

  const scaleWidth =
    ((width / METERS_PER_INCH) * POINTS_PER_INCH) / paperSize[0];
  const scaleHeight =
    ((height / METERS_PER_INCH) * POINTS_PER_INCH) / paperSize[1];
  const pageScale = contain
    ? Math.max(scaleWidth, scaleHeight)
    : Math.min(scaleWidth, scaleHeight);

  let optimal = SCALES[0];
  const scales = SCALES;
  for (let i = 0, n = scales.length; i < n; i++) {
    // If contain is true, we look for the scale immediately above
    // the page scale, else immediately below.
    if (pageScale > scales[i]) {
      if (contain) {
        if (i + 1 === n) {
          break;
        }
        optimal = scales[i + 1];
      } else {
        optimal = scales[i];
      }
    } else {
      break;
    }
  }
  return optimal;
}

export function getLayout(map: Map, orientation: Orientations) {
  const layout = ['A4', orientation];
  const layers = map.getLayers().getArray();
  const isSlowupLayerActive = layers.some(
    (layer) => layer.get('layername') === 'slowup' && layer.getVisible(),
  );
  if (isSlowupLayerActive) {
    layout.push('slowUp');
  }
  layout.push(getLanguageCode());

  return layout.join(' ');
}

// here
export function getOptimalScale(map: Map, orientation: Orientations) {
  const size = map.getSize();
  const resolution = map.getView().getResolution();
  const width = size[0] * resolution;
  const height = size[1] * resolution;
  return computeScale(getLayout(map, orientation), width, height);
}

export function getOptimalScaleForTour(
  map: Map,
  orientation: Orientations,
  trackSource: VectorSource,
) {
  const extent = trackSource.getExtent();
  const width = extent[2] - extent[0];
  const height = extent[3] - extent[1];
  return computeScale(getLayout(map, orientation), width, height, true);
}

export function getPrintExtent(
  map: Map,
  layout: string,
  scale: number,
  overrideCenter?: Coordinate,
): Extent {
  const center = overrideCenter ? overrideCenter : map.getView().getCenter();
  const paperSizePoints = getPaperSize(layout);
  const [paperWidthMeters, paperHeightMeters] = paperSizePoints.map(
    (side) => ((side / POINTS_PER_METER) * scale) / 2,
  );
  return [
    center[0] - paperWidthMeters,
    center[1] - paperHeightMeters,
    center[0] + paperWidthMeters,
    center[1] + paperHeightMeters,
  ];
}

export function postcompose(
  event: RenderEvent,
  map: Map,
  scale: number,
  orientation: Orientations,
) {
  const viewport = event.target.getViewport();
  const canvases = viewport.getElementsByTagName('canvas');

  const frameState = event.frameState;
  const resolution = frameState.viewState.resolution;
  const viewportWidth = Number(
    (frameState.size[0] * frameState.pixelRatio).toFixed(),
  );
  const viewportHeight = Number(
    (frameState.size[1] * frameState.pixelRatio).toFixed(),
  );

  // Topmost canvas were the mask will be drawn.
  // We need to use it's size to compute the pixel ratio:
  //  - on high dpi devices, the size is multiplied by pixelRatio if the layer is a vector layer but not if it's a raster layer.
  //  - on low dpi devices, the size is never multiplied by pixelRatio.
  const canvas = canvases.item(canvases.length - 1);
  const pixelRatio = (canvas.width / viewportWidth) * frameState.pixelRatio;
  console.assert(
    pixelRatio === (canvas.height / viewportHeight) * frameState.pixelRatio,
  );

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;

  const paperSizePoints = getPaperSize(getLayout(map, orientation));
  const [paperSizePixelWidth, paperSizePixelHeight] = paperSizePoints.map(
    (side) => (((side / POINTS_PER_METER) * scale) / resolution) * pixelRatio,
  );

  const minX = centerX - paperSizePixelWidth / 2;
  const minY = centerY - paperSizePixelHeight / 2;

  const context = canvas.getContext('2d');

  context.beginPath();

  // outer rectangle
  context.rect(0, 0, canvas.width, canvas.height);

  // inner rectangle
  context.rect(minX, minY, paperSizePixelWidth, paperSizePixelHeight);

  context.fillStyle = 'rgba(0, 5, 25, 0.5)';
  context.fill('evenodd');
}
